<template>
  <div class="flow-container">
    <!-- Background gradient -->
    <div class="tw-fixed tw-inset-0 tw-bg-gradient-to-br tw-from-blue-50 tw-via-white tw-to-purple-50"></div>
    
    <!-- Main content -->
    <div class="tw-relative tw-min-h-screen tw-flex tw-flex-col">
      <!-- Content Area -->
      <div class="tw-flex-1 tw-flex tw-items-center tw-justify-center tw-px-4 tw-py-12">
        <!-- Loading State -->
        <LoadingSpinner 
          v-if="loading"
          message="Loading your flow..."
        />

        <!-- Step Content -->
        <div
          v-else-if="currentStep"
          class="step-card"
        >
          <!-- Progress indicator -->
          <ProgressIndicator
            v-if="totalSteps > 0"
            :current-step="currentStepNumber"
            :total-steps="totalSteps"
          />

          <!-- Step Header -->
          <StepHeader :current-step="currentStep" />

          <!-- Yes/No Question Type -->
          <YesNoQuestion
            v-if="currentStep.step_type === 1"
            :current-step="currentStep"
            @yes="handleYes"
            @no="handleNo"
          />

          <!-- Form Type -->
          <FormStep
            v-if="currentStep.step_type === 2"
            :current-step="currentStep"
            v-model:form-data="formData"
            @submit="handleNextStepForm"
            @back="goToStep"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useCoreStore } from "@/stores/CoreStore";
import { useDesignerStore } from "@/stores/DesignerStore";
import LoadingSpinner from './components/LoadingSpinner.vue';
import ProgressIndicator from './components/ProgressIndicator.vue';
import StepHeader from './components/StepHeader.vue';
import YesNoQuestion from './components/YesNoQuestion.vue';
import FormStep from './components/FormStep.vue';

export default {
  name: 'FlowsPage',
  components: {
    LoadingSpinner,
    ProgressIndicator,
    StepHeader,
    YesNoQuestion,
    FormStep
  },
  inject: ['designerService'],
  setup() {
    const coreStore = useCoreStore();
    const designerStore = useDesignerStore();

    return {coreStore, designerStore};
  },
  data() {
    return {
      flowData: {
        id: 1,
        name: 'Demo Flow',
        uuid: 'cf99b7e9-20b5-43fd-b7be-6350d0610d12',
        first_step: 1,
      },
      currentStep: null,
      loading: true,
      formData: {},
      currentStepNumber: 1,
      totalSteps: 0,
    };
  },
  computed: {},
  async created() {
    if (this.coreStore.pageData.uuid) {
      // Initialize flow data in store
      this.designerStore.initFlow(this.coreStore.pageData.uuid, this.flowData.id);
      
      let flow = await this.designerService.loadFlow(this.coreStore.pageData.uuid)
      this.currentStep = flow.step;
      this.parseStep()
    }
  },
  methods: {
    async fetchStep(stepId) {
      this.loading = true;

      this.currentStep = await this.designerService.loadFlowStep(
          this.coreStore.pageData.uuid,
          stepId
      )

      this.parseStep()
    },
    parseStep() {
      if (this.currentStep.step_type === 2) {
        this.formData = this.currentStep.data.form.reduce((acc, field) => {
          acc[field.name] = '';
          return acc;
        }, {});
      }
      this.loading = false;
    },
    handleNextStepForm() {
      // Save form data to store before moving to next step
      this.designerStore.saveFormData(
        this.currentStep.id,
        this.currentStep.name,
        this.formData
      );
      
      const action = this.currentStep.data.form_next_step_action;
      if (action === 'step') {
        this.fetchStep(this.currentStep.data.form_next_step_step);
      } else if (action === 'url') {
        window.location.href = this.currentStep.data.form_next_step_url;
      } else if (action === 'lead_gen') {
        this.coreStore.pageData.uuid = this.currentStep.data.form_next_step_designer;
        this.coreStore.page = 'pg-lead-generator'
      }
    },
    handleYes() {
      // Save question answer to store
      const question = this.currentStep.data.yes_no_question || this.currentStep.data.question;
      this.designerStore.saveQuestionAnswer(
        this.currentStep.id,
        this.currentStep.name,
        question,
        'yes'
      );
      
      const action = this.currentStep.data.on_yes_action;
      if (action === 'step') {
        this.fetchStep(this.currentStep.data.on_yes_step);
      } else if (action === 'url') {
        window.location.href = this.currentStep.data.on_yes_url;
      } else if (action === 'lead_gen') {
        // Log flow data before transitioning
        console.log('Flow data collected:', this.designerStore.getFlowDataForSubmission());
        this.coreStore.pageData.uuid = this.currentStep.data.on_yes_designer;
        this.coreStore.page = 'pg-lead-generator'
      }
    },
    handleNo() {
      // Save question answer to store
      const question = this.currentStep.data.yes_no_question || this.currentStep.data.question;
      this.designerStore.saveQuestionAnswer(
        this.currentStep.id,
        this.currentStep.name,
        question,
        'no'
      );
      
      const action = this.currentStep.data.on_no_action;
      if (action === 'step') {
        this.fetchStep(this.currentStep.data.on_no_step);
      } else if (action === 'url') {
        window.location.href = this.currentStep.data.on_no_url;
      } else if (action === 'lead_gen') {
        // Log flow data before transitioning
        console.log('Flow data collected:', this.designerStore.getFlowDataForSubmission());
        this.coreStore.pageData.uuid = this.currentStep.data.on_no_designer;
        this.coreStore.page = 'pg-lead-generator'
      }
    },
    handleFormSubmit() {
      // Save form data to store
      this.designerStore.saveFormData(
        this.currentStep.id,
        this.currentStep.name,
        this.formData
      );
      
      console.log('Form Data Submitted:', this.formData);
      console.log('All Flow Data:', this.designerStore.getFlowDataForSubmission());
      this.fetchStep(this.currentStep.data.next_step);
    },
    goToStep(stepId) {
      this.fetchStep(stepId)
    }
  },
};
</script>

<style scoped>
/* Container and Layout */
.flow-container {
  @apply tw-relative tw-w-full tw-h-full;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
}

/* Step Card */
.step-card {
  @apply tw-w-full tw-max-w-2xl tw-bg-white tw-rounded-2xl tw-shadow-xl;
  @apply tw-overflow-hidden tw-transition-all tw-duration-300;
  animation: slideUp 0.4s ease-out;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive Design */
@media (max-width: 768px) {
  .step-card {
    @apply tw-max-w-full tw-mx-4;
  }
}

@media (max-width: 480px) {
  .step-card {
    @apply tw-rounded-xl;
  }
}

/* Print styles */
@media print {
  .step-card {
    @apply tw-shadow-none tw-border tw-border-gray-300;
  }
}
</style>
